import { Grid, LinearProgress } from "@mui/material";
import { ConfirmationDialog } from "klayowebshared";
import { AppContext } from "../../common/AppContext";
import axiosClient from "../../common/AxiosClient";
import { ErrorModel } from "../../common/models/ErrorModel";
import { AbstractDialog } from "../../components/AbstractDialog";
import { Alert } from "../../components/Alert";
import { DatePicker } from "../../components/DatePicker";
import { Dialog } from "../../components/dialogs/Dialog";
import { RequestActivityFeed } from "../../components/RequestActivityFeed";
import { AttributeSelector } from "../../components/selectors/AttributeSelector";
import { CourseSelector } from "../../components/selectors/CourseSelector";
import { ProficiencySelector } from "../../components/selectors/ProficiencySelector";
import { Snackbar } from "../../components/Snackbar";
import { TextField } from "../../components/TextField";
import { ValidationRequestKind } from "../../constants";
import { FeedbackAttribute } from "../../data/attributes/FeedbackAttribute";
import { Data } from "../../data/Data";
import { unFocusTableRowOnClose } from "../../utilities";

export class ReviewFeedbackDialog extends AbstractDialog {
  static contextType = AppContext;

  static mode = {
    add: {
      title: "Add new competency request",
      hash: "add"
    },
    training: {
      title: "Add new training request",
      hash: "add"
    },
    proficiencyChange: {
      title: "Request changes",
      hash: "edit"
    },
    feedback: {
      hash: "edit"
    }
  };

  constructor(props: any) {
    super(props);

    const requestKind = new URLSearchParams(window.location.search).get("requestKind");

    const initialMode =
      requestKind && Number(requestKind) === ValidationRequestKind.EmployeeTraining
        ? ReviewFeedbackDialog.mode.training
        : ReviewFeedbackDialog.mode.add;
    this.state = {
      ...this.state,
      error: null,
      attribute: null,
      downloadingDoc: null,
      originalProficiency: 0,
      mode: initialMode,
      showConfirmClose: false,
      comments: "",
      commentHasChange: false,
      newProficiency: 0,
      requestedProficiency: 0,
      lastMode: null,
      newExpiry: null,
      selectedTraining: null,
      selectedCourse: null,
      isLoading: false,
      requestKind: Number(requestKind)
    };
  }

  loadAttribute() {
    const { requestId, attributes } = this.props;
    const { requestKind } = this.state;

    if (!attributes) return;
    // this.setLoading("loadAttribute", true);
    this.setState({ isLoading: true });

    attributes
      .fetchAttributeByEmployeeAttributeId(this.context, requestId)
      .then((attribute: any) => {
        const mode =
          requestKind === ValidationRequestKind.EmployeeTraining
            ? ReviewFeedbackDialog.mode.training
            : attribute.type === FeedbackAttribute.type.newAttribute
              ? ReviewFeedbackDialog.mode.add
              : ReviewFeedbackDialog.mode.proficiencyChange;

        attribute[
          requestKind === ValidationRequestKind.EmployeeTraining
            ? "loadTrainingActivity"
            : "loadRequestActivity"
        ](this.context)
          .then((activity: any) => {
            this.setState({
              mode: mode,
              attribute: attribute,
              originalProficiency: attribute.currentProficiencyValue,
              requestedProficiency: attribute.proficiencyValue,
              newProficiency: attribute.proficiencyValue,
              newExpiry: attribute.expiryDate,
              selectedTraining:
                requestKind === ValidationRequestKind.EmployeeTraining
                  ? attribute.trainingId
                  : null,
              selectedCourse:
                requestKind === ValidationRequestKind.EmployeeTraining ? attribute.course : null
            });
          })
          .catch((e: any) => {
            alert(e.message);
          })
          .finally(() => this.setState({ isLoading: false }));
      })
      .catch((e: any) => {
        //this.props.onClose();
      });
  }

  getAttributeTypeLabel() {
    const { requestKind } = this.state;
    return requestKind === ValidationRequestKind.EmployeeTraining ? "training" : "competency";
  }

  componentDidMount() {
    this.loadAttribute();
  }

  componentDidUpdate(prevProps: any) {
    if (this.props.attributes !== prevProps.attributes) this.loadAttribute();
  }

  onDocumentClick(e: any, doc: any) {
    const { requestKind } = this.state;
    this.setLoading("downloadingDoc", true);
    this.setState({ downloadingDoc: doc });

    Data.call(
      "get",
      `/${requestKind === ValidationRequestKind.EmployeeAttribute ? "EmployeeAttribute" : "EmployeeTraining"}/download/` +
        doc.documentId,
      { responseType: "blob" }
    )
      .then((response) => {
        const blob = new Blob([response.data], { type: response.headers["content-type"] });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", doc.documentName);
        document.body.appendChild(link);
        link.click();
      })
      .catch((e) => {
        this.setState({ error: ErrorModel.parseServerError(e) });
      })
      .finally(() => this.setLoading("downloadingDoc", false));
  }

  onDocumentDownloadSnackbarClose() {
    this.setState({ downloadingDoc: null });
  }

  onProficiencyChange(e: any, proficiency: any) {
    this.setState({ newProficiency: proficiency });
  }

  onExpiryDateChange(date: any, valid: any) {
    this.setState({ newExpiry: date });
  }

  onCourseChange(e: any, course: any) {
    this.setState({ selectedCourse: course });
  }

  onAccept(e: any) {
    this.setState({ isLoading: true });
    const { attribute, requestKind } = this.state;

    const endpoint =
      requestKind === ValidationRequestKind.EmployeeTraining
        ? "/EmployeeTrainingValidationRequest/Accept"
        : "/EmployeeValidationRequest/Accept";

    const payload =
      requestKind === ValidationRequestKind.EmployeeTraining
        ? { employeeTrainingValidationRequestId: attribute.requestId }
        : { employeeAttributeValidationRequestId: attribute.requestId };

    axiosClient
      .post(endpoint, payload)
      .then((response: any) => {
        this.props.onClose(true);
      })
      .catch((error: any) => {
        this.setState({ error: ErrorModel.parseServerError(error) });
      })
      .finally(() => this.setState({ isLoading: false }));
  }

  onRequestChange(e: any) {
    this.setState({ lastMode: this.state.mode, mode: ReviewFeedbackDialog.mode.feedback });
  }

  hasChanges() {
    const { organization } = this.props;
    const {
      attribute,
      mode,
      requestedProficiency,
      newProficiency,
      commentHasChange,
      selectedCourse
    } = this.state;

    const commentsChange = commentHasChange;
    const hasProficiency =
      organization && organization.showProficiency && attribute && attribute.hasProficiency;
    const profChange = requestedProficiency !== newProficiency;
    // const courseChange = attribute && attribute.course !== selectedCourse;

    return (hasProficiency && profChange) || commentsChange;
  }

  onSendFeedback(e: any) {
    const { attribute, newProficiency, newExpiry, comments, requestKind } = this.state;

    // this.setLoading("accept", true);
    this.setState({ isLoading: true });

    //
    const endpoint =
      requestKind === ValidationRequestKind.EmployeeTraining
        ? "/EmployeeTrainingValidationRequest/RequestChange"
        : "/EmployeeValidationRequest/RequestChange";

    //
    const requestData =
      requestKind === ValidationRequestKind.EmployeeTraining
        ? {
            employeeTrainingValidationRequestId: attribute.requestId,
            // completionStatus: true,
            commentText: comments
          }
        : {
            employeeAttributeValidationRequestId: attribute.requestId,
            proficiencyValue: newProficiency,
            expiryDate: newExpiry,
            commentText: comments
          };

    axiosClient
      .post(endpoint, requestData)
      .then((response: any) => {
        this.props.onClose(true);
      })
      .catch((error: any) => {
        this.setState({ error: ErrorModel.parseServerError(error) });
      })
      .finally(() => this.setState({ isLoading: false }));
  }

  onCommentsChange(e: any) {
    this.setState({ comments: e.target.value, commentHasChange: true });
  }

  onClose() {
    const { readOnly } = this.props;

    unFocusTableRowOnClose();
    if (this.hasChanges() && !readOnly) this.setState({ showConfirmClose: true });
    else this.props.onClose(false);
  }

  onCancelClose() {
    this.setState({ showConfirmClose: false });
  }

  onAcceptClose() {
    this.setState({ showConfirmClose: false });
    this.props.onClose(false);
  }

  getDialogTitle() {
    const { mode, requestKind } = this.state;

    if (requestKind === ValidationRequestKind.EmployeeTraining) {
      if (mode === ReviewFeedbackDialog.mode.feedback) {
        return "Training Request Feedback";
      }
      if (mode === ReviewFeedbackDialog.mode.proficiencyChange) {
        return "Request changes";
      }
      return "Add new training request";
    } else {
      if (mode === ReviewFeedbackDialog.mode.feedback) {
        return "Competency Request Feedback";
      }
      return mode === ReviewFeedbackDialog.mode.add
        ? "Add new competency request"
        : "Request changes";
    }
  }

  render() {
    const { organization, user, employee, theme, readOnly } = this.props;
    const {
      error,
      attribute,
      downloadingDoc,
      originalProficiency,
      newProficiency,
      mode,
      lastMode,
      showConfirmClose,
      isLoading,
      comments,
      newExpiry,
      requestKind
    } = this.state;

    const allowSubmit = !isLoading;
    const isTrainingRequest = requestKind === ValidationRequestKind.EmployeeTraining;

    const actions = [];
    if (!readOnly) {
      if (mode !== ReviewFeedbackDialog.mode.feedback)
        actions.push({
          label: "Accept",
          variant: "filled",
          onClick: this.onAccept.bind(this),
          disabled: isLoading
        });

      if (mode !== ReviewFeedbackDialog.mode.feedback)
        actions.push({
          label: "Request changes",
          variant: "filled",
          onClick: this.onRequestChange.bind(this),
          disabled: isLoading
        });

      if (mode === ReviewFeedbackDialog.mode.feedback)
        actions.push({
          label: "Send feedback",
          variant: "filled",
          onClick: this.onSendFeedback.bind(this),
          disabled: !allowSubmit
        });
    }

    actions.push({
      label: readOnly ? "Close" : "Cancel",
      autoFocus: false,
      variant: readOnly ? "filled" : "outlined",
      onClick: this.onClose.bind(this),
      disabled: isLoading
    });

    const dialogTitle = this.getDialogTitle();

    return (
      <Dialog
        open={true}
        theme={theme}
        onClose={this.onClose.bind(this)}
        fullWidth={true}
        title={dialogTitle}
        maxWidth='sm'
        actions={actions}
      >
        {isLoading ? (
          <LinearProgress />
        ) : (
          <Grid container spacing={4}>
            {error && (
              <Grid item xs={12} md={12} lg={12}>
                <Alert severity='error'>{error}</Alert>
              </Grid>
            )}
            <Grid item xs={12} md={12} lg={12}>
              {isTrainingRequest ? (
                <CourseSelector
                  label='Training'
                  options={[]}
                  value={{
                    name: attribute?.attributeName,
                    id: attribute?.attributeId
                  }}
                  disabled={true}
                />
              ) : (
                <AttributeSelector attribute={attribute} allowSelection={false} />
              )}
            </Grid>

            {organization &&
              organization.showProficiency &&
              attribute &&
              attribute.hasProficiency && (
                <Grid item xs={12} md={12} lg={12}>
                  <ProficiencySelector
                    label={`${this.getAttributeTypeLabel()} Proficiency ${isTrainingRequest ? "" : "(required)"}`}
                    disabled={isLoading || mode !== ReviewFeedbackDialog.mode.feedback}
                    proficiency={newProficiency}
                    originalProficiency={
                      lastMode !== ReviewFeedbackDialog.mode.add ? originalProficiency : 0
                    }
                    forceShowOriginal={mode !== ReviewFeedbackDialog.mode.add}
                    onChange={this.onProficiencyChange.bind(this)}
                  />
                </Grid>
              )}

            {attribute && attribute.hasExpiryDate && (
              <Grid item xs={12} md={12} lg={12}>
                <DatePicker
                  theme={theme}
                  disabled={true} //isLoading || mode !== ReviewFeedbackDialog.mode.feedback}
                  label={`${isTrainingRequest ? "Completed date" : "Expiry Date"}`}
                  onChange={this.onExpiryDateChange.bind(this)}
                  value={newExpiry ? new Date(newExpiry) : null}
                  allowPast={false}
                  allowEmpty={true}
                  textFieldProps={{ fullWidth: true, readOnly: true }}
                />
              </Grid>
            )}

            {mode === ReviewFeedbackDialog.mode.feedback && (
              <Grid item xs={12} md={12} lg={12}>
                <TextField
                  id='klaro_attrchange_comments'
                  label='Comments (optional)'
                  multiline
                  disabled={isLoading || user === null}
                  rows={4}
                  placeholder={`Add any comments or notes about this ${isTrainingRequest ? "training" : "competency"} request`}
                  sx={{ width: "100%" }}
                  value={comments}
                  InputLabelProps={{ shrink: true }}
                  onChange={this.onCommentsChange.bind(this)}
                />
              </Grid>
            )}

            {attribute && attribute.requestActivity && (
              <RequestActivityFeed
                sx={{ marginTop: "-20px" }}
                user={user}
                employee={employee}
                activity={attribute.requestActivity}
                onDocumentClick={this.onDocumentClick.bind(this)}
              />
            )}
          </Grid>
        )}

        {showConfirmClose && (
          <ConfirmationDialog
            title='Cancel?'
            theme={theme}
            question={`Are you sure you want to cancel this ${this.getAttributeTypeLabel()} feedback?`}
            cancelButton='No'
            acceptButton='Yes'
            onCancel={this.onCancelClose.bind(this)}
            onAccept={this.onAcceptClose.bind(this)}
          />
        )}

        {downloadingDoc && (
          <Snackbar
            open={true}
            duration={6000}
            type='info'
            onClose={this.onDocumentDownloadSnackbarClose.bind(this)}
            message={"Downloaded document " + downloadingDoc.documentName}
          />
        )}
      </Dialog>
    );
  }
}
